/**
 * WorkspaceClosedOverlay
 *
 * Non-dismissible full-screen overlay shown when Central returns
 * 409 + code WORKSPACE_CLOSED.
 *
 * z-[9998] — below CentralUnavailableOverlay (z-[9999]).
 * Lists other workspaces the user can switch to.
 *
 * v2.3.0 — 2-step workspace switch:
 *   Step 1: POST /api/auth/select-workspace  { workspaceId }  → new token
 *   Step 2: GET  /api/suite/switch-context    (Bearer <new>)  → context
 *
 * IMPORTANT: Does NOT call centralLogout() during switch path.
 * Only the explicit "Sign out" button triggers logout.
 */

import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { AppDispatch } from '../../store';
import {
  selectCentralAccessToken,
  selectCurrentCentralWorkspace,
  selectOtherWorkspaces,
  resetBoot,
  setBootReady,
  setBootWorkspaceClosed,
  setBootUnavailable,
  setSwitchingWorkspace,
  centralLogout,
  updateCentralAccessToken,
} from '../../store/slices/centralAuthSlice';
import { api } from '../../store/api';
import { centralAuthApi } from '../../store/api/centralAuthApi';
import { CENTRAL_URL, APP_ID } from '../../config/centralAuth';

export function WorkspaceClosedOverlay() {
  const dispatch = useDispatch<AppDispatch>();
  const accessToken = useSelector(selectCentralAccessToken);
  const currentWorkspace = useSelector(selectCurrentCentralWorkspace);
  const otherWorkspaces = useSelector(selectOtherWorkspaces);
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const handleSwitchWorkspace = async (workspaceId: string) => {
    if (!accessToken || switchingId) return;
    setSwitchingId(workspaceId);
    setError('');

    // Set the switching guard to prevent any background 401 → logout race.
    dispatch(setSwitchingWorkspace(true));

    try {
      // ── Step 1: Get new token scoped to target workspace ──
      const selectRes = await fetch(`${CENTRAL_URL}/api/auth/select-workspace`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ workspaceId }),
      });

      // 401 → show error, do NOT logout
      if (selectRes.status === 401) {
        setError('Session expired — please sign out and log in again.');
        setSwitchingId(null);
        dispatch(setSwitchingWorkspace(false));
        return;
      }

      if (!selectRes.ok) {
        const errData = await selectRes.json().catch(() => ({}));
        console.error('[WorkspaceClosedOverlay] select-workspace failed:', selectRes.status, errData);
        setError('Failed to switch workspace. Please try again.');
        setSwitchingId(null);
        dispatch(setSwitchingWorkspace(false));
        return;
      }

      const selectData = await selectRes.json();
      const newToken: string = selectData.accessToken;

      if (!newToken) {
        setError('No token returned from Central.');
        setSwitchingId(null);
        dispatch(setSwitchingWorkspace(false));
        return;
      }

      // Store new token immediately
      dispatch(updateCentralAccessToken({
        accessToken: newToken,
        expiresIn: selectData.expiresIn,
      }));

      // ── Step 2: Get full context with the new token ──
      const contextRes = await fetch(`${CENTRAL_URL}/api/suite/switch-context`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${newToken}`,
          'X-App-Id': APP_ID,
        },
      });

      if (contextRes.ok) {
        const ctxData = await contextRes.json();
        if (ctxData.accessToken) {
          dispatch(updateCentralAccessToken({
            accessToken: ctxData.accessToken,
            expiresIn: ctxData.expiresIn,
          }));
        }
        // Clear caches before updating context
        dispatch(api.util.resetApiState());
        dispatch(centralAuthApi.util.resetApiState());
        dispatch(resetBoot());
        dispatch(setBootReady({
          currentWorkspace: ctxData.workspace,
          otherWorkspaces: ctxData.otherWorkspaces ?? [],
          apps: ctxData.apps ?? [],
          user: ctxData.user ?? null,
          meta: ctxData.meta,
        }));
        // Grace period before clearing switch guard
        setTimeout(() => {
          dispatch(setSwitchingWorkspace(false));
        }, 1500);
        return;
      }

      // 401 → show error, do NOT logout
      if (contextRes.status === 401) {
        setError('Session expired — please sign out and log in again.');
        setSwitchingId(null);
        dispatch(setSwitchingWorkspace(false));
        return;
      }

      if (contextRes.status === 409) {
        const data = await contextRes.json().catch(() => ({}));
        if (data.code === 'WORKSPACE_CLOSED') {
          dispatch(setBootWorkspaceClosed({ otherWorkspaces: data.otherWorkspaces ?? [] }));
          dispatch(setSwitchingWorkspace(false));
          return;
        }
      }

      // Other errors → CentralUnavailable
      dispatch(setBootUnavailable('Switch failed'));
      dispatch(setSwitchingWorkspace(false));
    } catch (err) {
      console.error('[WorkspaceClosedOverlay] Switch failed:', err);
      setError('Unable to reach Central. Please try again.');
      setSwitchingId(null);
      dispatch(setSwitchingWorkspace(false));
    }
  };

  // Only the explicit Sign Out button triggers logout
  const handleLogout = () => {
    dispatch(centralLogout());
  };

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-gray-900/80 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-8">
        <div className="text-center mb-6">
          <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900">Workspace Closed</h2>
          <p className="mt-2 text-sm text-gray-600">
            {currentWorkspace?.name ? (
              <>The workspace <strong>{currentWorkspace.name}</strong> has been closed.</>
            ) : (
              <>Your current workspace has been closed.</>
            )}
          </p>
        </div>

        {otherWorkspaces.length > 0 && (
          <div className="mb-6">
            <p className="text-sm font-medium text-gray-700 mb-3">Switch to another workspace:</p>
            <div className="space-y-2">
              {otherWorkspaces.map((ws) => (
                <button
                  key={ws.id}
                  onClick={() => handleSwitchWorkspace(ws.id)}
                  disabled={switchingId !== null}
                  className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors disabled:opacity-50"
                >
                  <span className="font-medium text-gray-900">{ws.name}</span>
                  <span className="ml-2 text-xs text-gray-500 capitalize">{ws.role}</span>
                  {switchingId === ws.id && (
                    <span className="ml-2 inline-block animate-spin h-3 w-3 border-2 border-primary-600 border-t-transparent rounded-full" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {otherWorkspaces.length === 0 && (
          <p className="text-sm text-gray-500 text-center mb-6">
            You have no other workspaces available. Contact your administrator.
          </p>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg text-center">
            {error}
          </div>
        )}

        <button
          onClick={handleLogout}
          className="w-full px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
